<template>
  <div>
    <navbar :links="links" />
    <!-- Header -->
    <v-layout
      align-center
      class="my-4"
    >
      <span class="grey--text text--darken-2 subheading">{{
        $t('brandingPageHeader')
      }}</span>
    </v-layout>
    <!-- Loading indicator -->
    <v-layout
      v-if="loading"
      justify-center
    >
      <v-progress-circular
        indeterminate
        style="position: fixed; z-index: 5"
        class="mt-5 msr--text"
        :size="50"
      />
    </v-layout>
    <div v-if="!loading && !brandings.length && !editedBranding">
      {{ $t('brandingNoneYet') }}.
      <a
        href="javascript:void(0)"
        @click="startCreateBranding()"
      >{{
        $t('brandingCreateNew')
      }}</a>
    </div>
    <!-- Existing Brandings -->
    <transition-group name="brandings-list-transition">
      <!-- Display Branding -->
      <branding-card
        v-for="(branding, brandingIndex) in brandings"
        :key="branding.id"
        :branding="branding"
        :is-editable="editedBranding === null"
        :show-divider="brandingIndex < brandings.length - 1"
        :show-toggle-default-button="
          brandings.length > 1 && !branding.is_default
        "
        data-cy="branding-card"
        @copy="startCopyBranding"
        @edit="startEditBranding"
        @toggledefault="toggleBrandingIsDefault(branding)"
        @delete="askDeleteBranding"
      />
    </transition-group>
    <!-- Create new Branding FAB -->
    <v-fade-transition>
      <v-layout
        v-if="editedBranding === null"
        justify-end
      >
        <v-tooltip left>
          <v-btn
            v-if="!loading"
            slot="activator"
            dark
            fab
            fixed
            :bottom="useFixedPositionForCreationButton"
            right
            class="msr"
            data-cy="new-branding-create-btn"
            :class="{
              'desktop-mode': $store.state.common.desktopMode,
              safari: $store.state.common.runtimeEnv.safari,
            }"
            @click="startCreateBranding()"
          >
            <v-icon medium>
              add
            </v-icon>
          </v-btn>
          <span>{{ $t('brandingCreateNew') }}</span>
        </v-tooltip>
      </v-layout>
    </v-fade-transition>
    <!-- Create/Edit Branding Dialog -->
    <create-edit-branding-dialog
      :show.sync="showCreateEditBrandingDialog"
      :branding="editedBranding"
      @abort="editedBranding = null"
      @save="onBrandingSaved"
    />
    <!-- Separate dialog to show animation after creating first Branding. -->
    <v-dialog
      v-model="showSuccessfullyCreatedFirstBrandingDialog"
      :width="480"
      lazy
      persistent
    >
      <v-card>
        <v-card-title class="pb-0">
          <v-layout align-center>
            <span class="title">{{
              $t('firstBrandingCreatedDialogTitle')
            }}</span>
            <v-btn
              class="ml-auto mr-0 my-0"
              flat
              icon
              :title="$t('close')"
              @click="onFirstBrandingCreatedDialogClosed()"
            >
              <v-icon>close</v-icon>
            </v-btn>
          </v-layout>
          <div class="subheading">
            {{ $t('firstBrandingCreatedDialogBody') }}
          </div>
        </v-card-title>
        <v-card-text class="py-0">
          <div
            class="clickable"
            :title="$t('goToTVTemplates')"
            data-cy="first-branding-created-dialog-success-animation"
            @click="onFirstBrandingCreatedDialogConfirmed()"
          >
            <!-- https://lottiefiles.com/64787-success -->
            <lottie-player
              v-if="lottiePlayer"
              :key="showSuccessfullyCreatedFirstBrandingDialog"
              src="https://assets2.lottiefiles.com/packages/lf20_y2hxPc.json"
              background="transparent"
              speed="0.66"
              :style="{
                'min-width': $vuetify.breakpoint.mdAndUp ? '448px' : 'initial',
                'min-height': $vuetify.breakpoint.mdAndUp ? '460px' : 'initial',
              }"
              style="width: 100%; height: auto"
              autoplay
            />
          </div>
        </v-card-text>
        <v-divider />
        <v-card-actions>
          <v-layout justify-center>
            <v-btn
              round
              dark
              color="msr"
              @click="onFirstBrandingCreatedDialogConfirmed()"
            >
              {{ $t('goToTVTemplates') }}
            </v-btn>
          </v-layout>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import {deepCopyObject} from '@/utils';
import {importLottiePlayer} from '@/deferredImports';

import {boxtypes} from '@/components/CreateEditBrandingDialog';
import BrandingCard from '@/components/BrandingCard';
import CreateEditBrandingDialog from '@/components/CreateEditBrandingDialog';
import Navbar from '@/components/Navbar';

const newBrandingDefaults = {
  buttonwidget_boxtype: boxtypes.ROUNDED,
  buttonwidget_color_background: null,
  buttonwidget_color_text: null,
  color_background: '#eeeeee',
  color_headline: '#000000',
  color_primary: null,
  color_secondary: null,
  font_name: 'Arial, sans-serif',
  id: null,
  is_default: false,
  logo: null,
  name: '',
  pricewidget_boxtype: boxtypes.ROUNDED,
  pricewidget_color_background: null,
  pricewidget_color_text: null,
  pricewidget_rebate_color_background: null,
  pricewidget_rebate_color_text: null,
};

export default {
  components: {
    BrandingCard,
    CreateEditBrandingDialog,
    Navbar,
  },
  data() {
    return {
      lottiePlayer: null,

      links: ['dashboard', 'branding'],
      loading: false,
      busy: false,
      showCreateEditBrandingDialog: false,
      showSuccessfullyCreatedFirstBrandingDialog: false,

      editedBranding: null,
    };
  },
  computed: {
    hasQueryParamCreateBranding() {
      return ['true', '1', true, 1].includes(this.$route.query.create);
    },
    brandings() {
      return this.$store.state.common.brandings;
    },
    defaultBrandingExists() {
      return (
        this.brandings.filter((branding) => branding.is_default).length > 0
      );
    },
    lastBrandingApplied() {
      return this.$store.state.common.lastBrandingApplied;
    },
    useFixedPositionForCreationButton() {
      return this.$vuetify.breakpoint.xsOnly || this.brandings.length >= 3;
    },
  },
  created() {
    if (!this.$route.query.edit) {
      this.fetchBrandings();
    }
  },
  mounted() {
    importLottiePlayer().then((lib) => (this.lottiePlayer = lib));
  },
  methods: {
    fetchBrandings() {
      this.loading = true;
      return this.$store.dispatch('LIST_BRANDINGS').finally(() => {
        this.loading = false;
      });
    },
    startCreateBranding() {
      this.editedBranding = deepCopyObject(newBrandingDefaults);
      if (!this.defaultBrandingExists) {
        this.editedBranding.is_default = true;
      }
      this.showCreateEditBrandingDialog = true;
    },
    startEditBranding(branding) {
      this.editedBranding = deepCopyObject(branding);
      this.showCreateEditBrandingDialog = true;
    },
    startCopyBranding(branding) {
      const copiedBranding = deepCopyObject(branding);
      copiedBranding.id = null;
      copiedBranding.is_default = false;
      copiedBranding.name += ' (' + this.$t('aCopy') + ')';
      this.startEditBranding(copiedBranding);
    },
    toggleBrandingIsDefault(branding) {
      const modifiedBranding = deepCopyObject(branding);
      modifiedBranding.is_default = !modifiedBranding.is_default;
      this.onBrandingSaved(modifiedBranding);
    },
    handleDefaultBrandingMayHaveChanged(branding) {
      // Ensure changing the default Branding will apply that one next
      // time as needed e.g. on the TVTemplates list.
      if (
        branding.is_default &&
        (this.lastBrandingApplied || {}).id !== branding.id
      ) {
        this.$store.commit('SET_LAST_BRANDING_APPLIED', branding);
      }
    },
    onBrandingSaved(branding) {
      const isNew = branding && branding.id === null;
      if (isNew) {
        this.onCreateNewBrandingConfirmed(branding);
        return;
      }
      this.onEditBrandingConfirmed(branding);
    },
    onCreateNewBrandingConfirmed(branding) {
      let numBrandings = this.brandings.length;

      this.busy = true;
      this.$api
        .createBranding(branding)
        .then((response) => {
          this.handleDefaultBrandingMayHaveChanged(response.data);
          this.fetchBrandings();
          this.reset();

          numBrandings += 1;

          if (numBrandings < 2) {
            this.showSuccessfullyCreatedFirstBrandingDialog = true;
          }
        })
        .finally(() => {
          this.busy = false;
        });
    },
    onEditBrandingConfirmed(branding) {
      this.busy = true;
      this.$api
        .updateBranding(branding.id, branding)
        .then((response) => {
          this.handleDefaultBrandingMayHaveChanged(response.data);
          this.fetchBrandings();
          this.reset();
        })
        .finally(() => {
          this.busy = false;
        });
    },
    reset() {
      this.editedBranding = null;
    },
    askDeleteBranding(branding) {
      const confirmed = confirm(this.$t('brandingDeleteQuestion'));
      if (!confirmed) {
        return;
      }
      this.onDeleteBrandingConfirmed(branding);
    },
    onDeleteBrandingConfirmed(branding) {
      this.busy = true;
      this.$api
        .deleteBranding(branding.id)
        .then(() => {
          if (branding.is_default) {
            this.$store.commit('SET_LAST_BRANDING_APPLIED', null);
          }
          this.fetchBrandings();
        })
        .finally(() => {
          this.busy = false;
        });
    },
    handleDialogOpenedQuery() {
      if (this.hasQueryParamCreateBranding) {
        this.startCreateBranding();
      } else if (this.$route.query.edit) {
        const brandingId = this.$route.query.edit;
        this.fetchBrandings().then(() => {
          const branding = this.brandings.filter(
            (branding) => branding.id == brandingId
          )[0];
          if (branding) {
            this.startEditBranding(branding);
          }
        });
      }
    },
    onFirstBrandingCreatedDialogClosed() {
      this.showSuccessfullyCreatedFirstBrandingDialog = false;
    },
    onFirstBrandingCreatedDialogConfirmed() {
      this.onFirstBrandingCreatedDialogClosed();

      // Redirect user to tvtemplates page if this is his first Branding.
      this.$router.push({name: 'tvtemplates'});
    },
  },
  beforeRouteEnter(to, from, next) {
    next((vm) => {
      vm.handleDialogOpenedQuery();
    });
  },
};
</script>

<style scoped>
.brandings-list-transition-enter-active,
.brandings-list-transition-leave-active {
  transition: all 0.25s ease-in-out;
}
.brandings-list-transition-enter,
.brandings-list-transition-leave-to {
  opacity: 0;
  transform: translateX(50px);
}
</style>



// WEBPACK FOOTER //
// src/pages/Brandings.vue